import React, { useEffect, useMemo, useRef, useState } from "react"

import { ScheduleCatalogService } from "../../services/v2/scheduleCatalog.service"
import "./filtroHorarios.css"

/*
	Filtro de Estudiantes por Curso, Periodo y Horario, con los mismos botones del Catálogo de
	Horarios (Esteban, 2026-09-24). Cada botón abre una lista con casillas; se puede marcar varias.
	Lo que se elige en Curso acota Periodo y Horario, y lo de Periodo acota Horario, para que
	nunca se ofrezca una combinación que no existe.
*/

export interface SeleccionHorarios {
	courseNames: string[]
	courseIds: number[]
	periodIds: number[]
	scheduleIds: number[]
}

export const SIN_SELECCION: SeleccionHorarios = { courseNames: [], courseIds: [], periodIds: [], scheduleIds: [] }

interface Opcion { value: any, label: string }

const sinAcentos = (t: string) => String(t || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim()

interface PildoraProps {
	etiqueta: string
	opciones: Opcion[]
	elegidos: any[]
	abierta: boolean
	onAbrir: () => void
	onCambio: (valores: any[]) => void
	anchoPanel?: number
}

/** El botón con su lista de casillas; el estado vive afuera. */
const Pildora: React.FC<PildoraProps> = ({ etiqueta, opciones, elegidos, abierta, onAbrir, onCambio, anchoPanel }) => {
	const [busqueda, setBusqueda] = useState("")
	useEffect(() => { if (!abierta) { setBusqueda("") } }, [abierta])
	const aguja = sinAcentos(busqueda)
	const visibles = aguja === "" ? opciones : opciones.filter((o) => sinAcentos(o.label).includes(aguja))
	const cuantos = elegidos.length

	return <div className="wfhDrop" onClick={(e) => e.stopPropagation()}>
		<button type="button" className={"wfhDropBtn" + (cuantos > 0 ? " wfhDropBtnOn" : "")} onClick={onAbrir}>
			{cuantos > 0 ? `${etiqueta} (${cuantos})` : etiqueta}
		</button>
		{!abierta ? null : <div className="wfhDropPanel" style={anchoPanel ? { width: anchoPanel } : undefined}>
			{opciones.length > 8
				? <input type="search" className="wfhDropFind" placeholder="Buscar…" value={busqueda} autoFocus
					onChange={(e) => setBusqueda(e.target.value)} />
				: null}
			{visibles.length < 1 ? <div className="wfhVacio">{opciones.length < 1 ? "No hay opciones" : "Nada coincide"}</div> : null}
			{visibles.map((o) => {
				const marcada = elegidos.includes(o.value)
				return <label key={String(o.value)} className="wfhDropItem">
					<input type="checkbox" checked={marcada}
						onChange={() => onCambio(marcada ? elegidos.filter((v) => v !== o.value) : [...elegidos, o.value])} />
					<span>{o.label}</span>
				</label>
			})}
			{cuantos < 1 ? null : <button type="button" className="wfhDropClear" onClick={() => onCambio([])}>Quitar selección</button>}
		</div>}
	</div>
}

interface FiltroHorariosProps {
	campusId: number
	seleccion: SeleccionHorarios
	onCambio: (seleccion: SeleccionHorarios) => void
}

const servicio = new ScheduleCatalogService()

const FiltroHorarios: React.FC<FiltroHorariosProps> = ({ campusId, seleccion, onCambio }) => {
	const [datos, setDatos] = useState<any>(null)
	const [abierta, setAbierta] = useState<string | null>(null)
	const raiz = useRef<HTMLDivElement>(null)

	useEffect(() => {
		let vivo = true
		setDatos(null)
		servicio.studentFilterOptions(campusId)
			.then((r) => { if (vivo) { setDatos(r.data) } })
			.catch(() => { if (vivo) { setDatos({ cursos: [], periodos: [], horarios: [] }) } })
		return () => { vivo = false }
	}, [campusId])

	useEffect(() => {
		const cerrar = () => setAbierta(null)
		document.addEventListener("click", cerrar)
		return () => document.removeEventListener("click", cerrar)
	}, [])

	const cursosElegidos: any[] = useMemo(() => (datos?.cursos || []).filter((c: any) => seleccion.courseNames.includes(c.nombre)), [datos, seleccion.courseNames])
	const idsCurso: number[] = cursosElegidos.flatMap((c: any) => c.ids)

	const horariosDelCurso: any[] = (datos?.horarios || []).filter((h: any) => idsCurso.length < 1 || idsCurso.includes(h.cursoId))
	const periodosPosibles = new Set(horariosDelCurso.map((h: any) => h.periodoId))
	const opcionesPeriodo: Opcion[] = (datos?.periodos || []).filter((p: any) => periodosPosibles.has(p.id)).map((p: any) => ({ value: p.id, label: p.nombre }))
	const horariosPosibles: any[] = horariosDelCurso.filter((h: any) => seleccion.periodIds.length < 1 || seleccion.periodIds.includes(h.periodoId))
	const opcionesHorario: Opcion[] = horariosPosibles.map((h: any) => ({
		value: h.id,
		label: [h.modulo || h.curso, h.horario, "inicia " + h.inicioTexto, h.status && h.status !== "Activo" ? h.status : ""].filter((t: string) => t).join(" · ")
	}))

	//Al cambiar Curso o Periodo se sueltan las elecciones que quedaron fuera de alcance.
	const emitir = (courseNames: string[], periodIds: number[], scheduleIds: number[]) => {
		const cursos = (datos?.cursos || []).filter((c: any) => courseNames.includes(c.nombre))
		const ids: number[] = cursos.flatMap((c: any) => c.ids)
		const horarios = (datos?.horarios || []).filter((h: any) => ids.length < 1 || ids.includes(h.cursoId))
		const periodosValidos = new Set(horarios.map((h: any) => h.periodoId))
		const periodos = periodIds.filter((id) => periodosValidos.has(id))
		const horariosValidos = new Set(horarios.filter((h: any) => periodos.length < 1 || periodos.includes(h.periodoId)).map((h: any) => h.id))
		onCambio({ courseNames, courseIds: ids, periodIds: periodos, scheduleIds: scheduleIds.filter((id) => horariosValidos.has(id)) })
	}

	if (datos == null) {
		return <div className="wfhBarra"><span className="wfhCargando">Cargando cursos y horarios…</span></div>
	}
	const hayAlgo = seleccion.courseNames.length + seleccion.periodIds.length + seleccion.scheduleIds.length > 0
	const alternar = (nombre: string) => setAbierta(abierta === nombre ? null : nombre)

	return <div className="wfhBarra" ref={raiz}>
		<span className="wfhEtiqueta">Filtrar por</span>
		<Pildora etiqueta="Curso" abierta={abierta === "curso"} onAbrir={() => alternar("curso")}
			opciones={(datos.cursos || []).map((c: any) => ({ value: c.nombre, label: c.nombre }))}
			elegidos={seleccion.courseNames} onCambio={(v) => emitir(v, seleccion.periodIds, seleccion.scheduleIds)} />
		<Pildora etiqueta="Periodo" abierta={abierta === "periodo"} onAbrir={() => alternar("periodo")}
			opciones={opcionesPeriodo} elegidos={seleccion.periodIds}
			onCambio={(v) => emitir(seleccion.courseNames, v, seleccion.scheduleIds)} />
		<Pildora etiqueta="Horario" abierta={abierta === "horario"} onAbrir={() => alternar("horario")} anchoPanel={420}
			opciones={opcionesHorario} elegidos={seleccion.scheduleIds}
			onCambio={(v) => emitir(seleccion.courseNames, seleccion.periodIds, v)} />
		{hayAlgo ? <button type="button" className="wfhLimpiar" onClick={() => onCambio(SIN_SELECCION)}>Quitar filtros</button> : null}
	</div>
}

export default FiltroHorarios
